import React, { useState } from 'react';
import { AltDataPoint } from '../types';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Label } from 'recharts';
import { Calculator, Save, RefreshCw } from 'lucide-react';
import { authenticatedFetchJSON } from '../utils/authenticatedFetch';

interface AltAnalysisProps {
  data: AltDataPoint[];
  setData: React.Dispatch<React.SetStateAction<AltDataPoint[]>>;
}

export const AltAnalysis: React.FC<AltAnalysisProps> = ({ data, setData }) => {
  const [newStress, setNewStress] = useState<string>('');
  const [newCycles, setNewCycles] = useState<string>('');

  const handleAddPoint = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStress || !newCycles) return;

    const point: AltDataPoint = {
      id: Date.now().toString(),
      stressLevel: parseFloat(newStress),
      cyclesToFailure: parseFloat(newCycles),
      censored: false
    };

    setData([...data, point]);
    setNewStress('');
    setNewCycles('');
  };

  // Simple regression for display (Arrhenius-like linear fit on Log-Linear for demo)
  // In reality, you'd linearize: ln(Life) = A + B * (1/T)
  // For visualization, we just plot the scatter.

  const handleRecalculate = async () => {
    try {
      alert('🔄 Recalculating ALT Model...\n\nRunning Arrhenius regression on your current data points to derive life-stress parameters.');
      const result = await authenticatedFetchJSON('/api/v1/lab/calculate-acceleration', {
        method: 'POST',
        body: JSON.stringify({
          model: 'arrhenius',
          parameters: {
            data: data,
            temp_use: 25,
            temp_stress: data[data.length - 1]?.stressLevel || 85
          }
        })
      });
      console.log("✅ ALT Recalculation Result:", result);
      alert(`Model Recalculated!\n\nAcceleration Factor: ${parseFloat(result).toFixed(2)}`);
    } catch (e) {
      console.error("ALT Recalculation Failed", e);
    }
  };

  return (
    <div className="h-full flex flex-col lg:flex-row gap-6">
      {/* Left Panel: Controls & Data */}
      <div className="w-full lg:w-1/3 flex flex-col gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Calculator className="text-indigo-500" size={20} />
            Data Entry
          </h2>
          <form onSubmit={handleAddPoint} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Stress Level (Temp °C)</label>
              <input
                type="number"
                value={newStress}
                onChange={(e) => setNewStress(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                placeholder="e.g. 85"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Cycles to Failure</label>
              <input
                type="number"
                value={newCycles}
                onChange={(e) => setNewCycles(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                placeholder="e.g. 15000"
              />
            </div>
            <button
              type="submit"
              className="w-full py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium flex justify-center items-center gap-2"
            >
              <Save size={16} />
              Add Data Point
            </button>
          </form>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex-1 flex flex-col overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
            <h3 className="font-semibold text-slate-800 text-sm">Input Data</h3>
          </div>
          <div className="overflow-y-auto flex-1 custom-scrollbar p-0">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 sticky top-0">
                <tr>
                  <th className="px-6 py-2 text-left">Stress</th>
                  <th className="px-6 py-2 text-left">Cycles</th>
                  <th className="px-6 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.map(pt => (
                  <tr key={pt.id}>
                    <td className="px-6 py-3">{pt.stressLevel}°C</td>
                    <td className="px-6 py-3">{pt.cyclesToFailure}</td>
                    <td className="px-6 py-3 text-right">
                      <button
                        onClick={() => setData(data.filter(d => d.id !== pt.id))}
                        className="text-red-500 hover:text-red-700 text-xs font-medium"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
                {data.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-6 py-8 text-center text-slate-400">No data points added.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Right Panel: Analysis Chart */}
      <div className="w-full lg:w-2/3 flex flex-col gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex-1 min-h-[500px] flex flex-col">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Life-Stress Relationship</h2>
              <p className="text-sm text-slate-500">Arrhenius Model Plot (Temperature vs Life)</p>
            </div>
            <button
              onClick={handleRecalculate}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            >
              <RefreshCw size={16} />
              Recalculate Model
            </button>
          </div>

          <div className="flex-1 w-full relative">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" dataKey="stressLevel" name="Temperature" unit="°C">
                  <Label value="Stress Level (Temp °C)" offset={-10} position="insideBottom" />
                </XAxis>
                <YAxis type="number" dataKey="cyclesToFailure" name="Life" unit=" cyc" scale="log" domain={['auto', 'auto']}>
                  <Label value="Cycles to Failure (Log Scale)" angle={-90} position="insideLeft" />
                </YAxis>
                <Tooltip cursor={{ strokeDasharray: '3 3' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                <Scatter name="Failures" data={data} fill="#6366f1" shape="circle" />
                {/* 
                   In a real app, we would calculate the regression line points here based on 
                   Arrhenius equation parameters derived from the input data.
                 */}
              </ScatterChart>
            </ResponsiveContainer>

            {data.length < 2 && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-sm z-10">
                <div className="text-center p-6 bg-white rounded-xl shadow-lg border border-slate-200">
                  <p className="text-slate-600 font-medium">Insufficient Data</p>
                  <p className="text-sm text-slate-400 mt-1">Add at least 2 data points to visualize the model.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};